import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';

/**
 * Les résultats d'un examen national, pour la vue « Résultats » de sa liste.
 *
 * Chargés à la première ouverture de la vue seulement : la plupart des
 * passages dans un examen servent aux dossiers, et la requête compte toute la
 * liste pour la synthèse.
 *
 * `disponible` vaut null tant qu'on ne sait pas, false quand la migration
 * n'est pas passée sur l'école (l'écran le dit, au lieu d'afficher une liste
 * de « non renseigné » que personne ne pourrait remplir).
 *
 * `parEleve` : { [eleve_id]: ligne de résultat }. Un élève absent de l'objet
 * n'a pas encore de résultat.
 */
const ETAT_INITIAL = { disponible: null, peutSaisir: false, parEleve: {}, synthese: null, dernierImport: null };

export const useResultatsExamen = ({ code, actif, onErreur = null }) => {
  const [etat, setEtat] = useState(ETAT_INITIAL);
  const [chargement, setChargement] = useState(false);
  const [codeCharge, setCodeCharge] = useState(null);

  // `onErreur` change à chaque rendu du parent : le passer en dépendance
  // relancerait le chargement.
  const rappelErreur = useRef(onErreur);
  rappelErreur.current = onErreur;

  // Le code de l'examen affiché : une réponse arrivée après un changement
  // d'examen ne doit pas remplir la liste du suivant.
  const codeCourant = useRef(code);
  codeCourant.current = code;

  // Un échec de lecture laisse l'écran sans liste : il est dit sur place, avec
  // de quoi réessayer, et n'est pas relancé tout seul en boucle.
  const [echec, setEchec] = useState(false);

  // Seule la dernière requête lancée a le droit de lever le drapeau de
  // chargement : sinon une réponse lente de l'examen précédent le laisserait
  // levé pour toujours, ou le baisserait pendant la lecture du suivant.
  const derniereRequete = useRef(0);

  const charger = useCallback(async () => {
    const codeDemande = code;
    const numero = ++derniereRequete.current;
    const estCourante = () => numero === derniereRequete.current && codeCourant.current === codeDemande;
    setChargement(true);
    setEchec(false);
    try {
      const reponse = await api.examens.getResultats(codeDemande);
      if (!estCourante()) return;
      if (!reponse?.success) {
        throw new Error(reponse?.message || reponse?.error || 'Lecture des résultats impossible');
      }
      const {
        disponible,
        peut_saisir: peutSaisir,
        resultats = [],
        synthese = null,
        dernier_import: dernierImport = null
      } = reponse.data || {};
      setEtat({
        disponible: Boolean(disponible),
        peutSaisir: Boolean(peutSaisir),
        parEleve: Object.fromEntries(resultats.map((r) => [r.eleve_id, r])),
        synthese,
        dernierImport
      });
      setEchec(false);
    } catch (erreur) {
      if (estCourante()) {
        setEchec(true);
        rappelErreur.current?.(erreur.message || 'Lecture des résultats impossible');
      }
    } finally {
      // Le code réellement lu : s'il n'est plus celui affiché, l'effet
      // ci-dessous relance aussitôt la lecture du bon examen.
      if (numero === derniereRequete.current) {
        setCodeCharge(codeDemande);
        setChargement(false);
      }
    }
  }, [code]);

  // Changer d'examen repart de zéro : les résultats du BEPC ne doivent pas
  // s'afficher, même un instant, sur la liste du BAC.
  useEffect(() => {
    setEtat(ETAT_INITIAL);
    setEchec(false);
    setCodeCharge(null);
  }, [code]);

  useEffect(() => {
    if (actif && codeCharge !== code && !chargement) charger();
  }, [actif, code, codeCharge, chargement, charger]);

  /**
   * Enregistre ce qui change sur un élève (seuls les champs passés changent,
   * null efface). La ligne et la synthèse suivent la réponse du serveur.
   * Lève une erreur portant le message du serveur en cas de refus.
   */
  const enregistrer = useCallback(async (eleveId, champs) => {
    const reponse = await api.examens.enregistrerResultat({
      examen_type: code,
      eleve_id: eleveId,
      ...champs
    });
    if (!reponse?.success) {
      throw new Error(reponse?.message || reponse?.error || "L'enregistrement a échoué");
    }
    if (codeCourant.current !== code) return reponse;

    setEtat((precedent) => {
      const parEleve = { ...precedent.parEleve };
      if (reponse.data?.resultat) parEleve[eleveId] = reponse.data.resultat;
      else delete parEleve[eleveId];
      return { ...precedent, parEleve, synthese: reponse.data?.synthese ?? precedent.synthese };
    });
    return reponse;
  }, [code]);

  return { ...etat, chargement, echec, recharger: charger, enregistrer };
};

export default useResultatsExamen;
